import defaultLocationSearches from './configDefaultLocationSearches';

//////////////////////////////////////////////////////
// Configurations related to maps and map providers //
//////////////////////////////////////////////////////

// Note: The mapboxAccessToken & googleMapsAPIKey come from map asset nowadays by default.
//       To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeMapConfig func)
export const mapboxAccessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
export const googleMapsAPIKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Choose map provider: 'mapbox', 'googleMaps'
// Note: The mapProvider comes from map asset nowadays by default.
//       To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeMapConfig func)
export const mapProvider = 'mapbox';

// Search radius in meters (half-extent of the bounding box, applied in each
// direction from the searched point) used when a shopper searches by current
// location or by a town/zip. Wide enough that nearby parks appear without the
// shopper having to zoom out. Override via env var.
const DEFAULT_SEARCH_RADIUS_METERS = 50000;
const parsedSearchRadiusMeters = Number(process.env.REACT_APP_MAP_SEARCH_RADIUS_METERS);
const searchRadiusMeters =
  Number.isFinite(parsedSearchRadiusMeters) && parsedSearchRadiusMeters > 0
    ? parsedSearchRadiusMeters
    : DEFAULT_SEARCH_RADIUS_METERS;

// Countries the location autocomplete is limited to (ISO 3166 alpha-2 codes).
// Override via env var with a comma-separated list, e.g. "US,CA".
const DEFAULT_COUNTRY_LIMIT = ['US'];
const countryLimitFromEnv = process.env.REACT_APP_MAP_COUNTRY_LIMIT;
const countryLimit = countryLimitFromEnv
  ? countryLimitFromEnv
      .split(',')
      .map(code => code.trim())
      .filter(Boolean)
  : DEFAULT_COUNTRY_LIMIT;

// The location search input can be configured to show default
// searches when the user focuses on the input and hasn't yet typed
// anything. This reduces typing and avoids too many Geolocation API
// calls for common searches.
export const search = {
  // When enabled, the first suggestion is "Current location" that
  // uses the browser Geolocation API to query the user's current
  // location.
  suggestCurrentLocation: true,

  // Distance in meters for calculating the bounding box around the
  // current location. Configurable via REACT_APP_MAP_SEARCH_RADIUS_METERS.
  currentLocationBoundsDistance: searchRadiusMeters,

  // This affects location search.
  // These "default locations" can be edited in the
  // `configDefaultLocationSearches.js` file.
  // NOTE: these are highly recommended, since they
  //       1) help customers to find relevant locations, and
  //       2) reduce the cost of using map providers geocoding API
  defaults: defaultLocationSearches || [],

  // Should search results be ordered by distance to origin.
  // NOTE 1: This doesn't affect if the main search type is 'keywords'
  // NOTE 2: If this is set to true add parameter 'origin' to every location in configDefaultLocationSearches.js
  //         Without the 'origin' parameter, search will not work correctly
  // NOTE 3: Keyword search and ordering search results by distance can't be used at the same time.
  //         You should check that keyword filter is not used in configSearch.js
  sortSearchByDistance: false,

  // Limit location autocomplete to one or more countries using ISO 3166
  // alpha-2 country codes. Configurable via REACT_APP_MAP_COUNTRY_LIMIT.
  countryLimit,
};

// When fuzzy locations are enabled, coordinates on maps are
// obfuscated randomly around the actual location.
//
// NOTE: This only hides the locations in the UI level, the actual
// coordinates are still accessible in the HTTP requests and the
// Redux store.
export const fuzzy = {
  enabled: false,

  // Amount of maximum offset in meters that is applied to obfuscate
  // the original coordinates. The actual value is random, but the
  // obfuscated coordinates are withing a circle that has the same
  // radius as the offset.
  offset: 500,

  // Default zoom level when showing a single circle on a Map. Should
  // be small enough so the whole circle fits in.
  defaultZoomLevel: 13,

  // Color of the circle on the Map component.
  circleColor: '#c0392b',
};
